import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../store";
import { useCart } from "../../../context/CartContext";
import { ProductType } from "../../../Redux/custmoer_cart_service/types";
import { getDigitalProductById } from "../../../Redux/digital-product/slice";
import DatePicker from "../../globalcomponents/datepicker/DatePicker";
import CategoriesBar from "../../globalcomponents/navbar/categorieslist/CategoriesBar";
import Navbar from "../../globalcomponents/navbar/Navbar";
import TimeSlots from "./TimeSlots";
import { format, addDays } from "date-fns";
import { useAuth } from "../../../context/AuthContext";
import {
  fetchAllCountries,
  fetchStatesByCountryName,
  fetchDistrictsByStateName,
} from "../../../Redux/location-mangment/slice";

// ── Reusable SearchDropdown (Extracted from PersonalDetails) ──────────────────
interface SearchDropdownProps {
  label: string;
  value: string;
  options: string[];
  onSelect: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
}

const SearchDropdown: React.FC<SearchDropdownProps> = ({
  label,
  value,
  options,
  onSelect,
  disabled = false,
  loading = false,
  placeholder = "Search...",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = options.filter((o) =>
    o.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (option: string) => {
    onSelect(option);
    setOpen(false);
    setSearch("");
  };

  return (
    <div className="position-relative w-100 mb-3" ref={containerRef}>
      <div
        className={`input-group input-group-outline ${value || open ? "focused is-focused" : ""}`}
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
        onClick={() => {
          if (!disabled) setOpen((p) => !p);
        }}
      >
        <label className="form-label">
          <b className="fw-bold text-black mt-0">{label}</b>
        </label>
        <input
          type="text"
          className="form-control"
          value={open ? search : value}
          readOnly={!open}
          placeholder={open ? placeholder : ""}
          onChange={(e) => setSearch(e.target.value)}
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) setOpen(true);
          }}
          style={{ cursor: disabled ? "not-allowed" : "text" }}
        />
        <div>
          <span className="input-group-text p-1 me-1">
            {loading ? (
              <span
                className="spinner-border spinner-border-sm opacity-5"
                role="status"
              />
            ) : (
              <i
                className={`ri-arrow-${open ? "up" : "down"}-s-line fs-4 mt-1 cursor-pointer opacity-4`}
              />
            )}
          </span>
        </div>
      </div>

      {open && (
        <div
          className="border border-radius-lg shadow-lg bg-white position-absolute w-100 z-index-sticky"
          style={{
            top: "100%",
            maxHeight: 220,
            overflowY: "auto",
            zIndex: 9999,
          }}
        >
          {loading ? (
            <div className="text-center py-3 text-sm text-muted">
              Loading...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-3 text-sm text-muted">
              No results
            </div>
          ) : (
            filtered.map((option) => (
              <div
                key={option}
                className="dropdown-item px-3 py-2 cursor-pointer"
                style={{ fontSize: "0.875rem" }}
                onMouseDown={() => handleSelect(option)}
              >
                {option}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
// ────────────────────────────────────────────────────────────────────────────

const CustomerDetails = () => {
  const { sellerId, productId } = useParams<{ sellerId: string; productId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const { addItemToCart } = useCart();
  const user = useSelector((state: RootState) => state.customerAuth.user);
  const { selectedSeller } = useSelector((state: RootState) => state.segment);
  const { currentProduct } = useSelector((state: RootState) => state.digitalProduct);
  const { countries, states, districts, loading: locLoading } = useSelector((state: RootState) => state.location);

  // Form State
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    dob: undefined as Date | undefined,
    mobile: "",
    email: "",
    address: "",
    country: "",
    state: "",
    district: "",
    city: "",
    pincode: "",
    consultationMode: "Physical",
  });

  // Booking State
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedSlot, setSelectedSlot] = useState("");
  const [activeDateIndex, setActiveDateIndex] = useState(0);

  const dates = [0, 1, 2, 3, 4, 5].map(d => addDays(new Date(), d));

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/SignIn");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (countries.length === 0 && !locLoading) dispatch(fetchAllCountries());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.name || "",
        lastName: user.fullName || "",
        gender: user.gender || "",
        dob: user.dob ? new Date(user.dob) : undefined,
        mobile: user.phone || "",
        email: user.email || "",
        country: user.country || "",
        state: user.state || "",
        district: user.district || "",
        address: user.zipCode || "",
        city: user.city || "",
        pincode: user.zipCode || "",
        consultationMode: "Physical",
      });

      if (user.country) dispatch(fetchStatesByCountryName(user.country));
      if (user.state) dispatch(fetchDistrictsByStateName(user.state));
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (productId) {
      dispatch(getDigitalProductById(productId));
    }
  }, [productId, dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCountrySelect = (country: string) => {
    setFormData({ ...formData, country, state: "", district: "" });
    dispatch(fetchStatesByCountryName(country));
  };

  const handleStateSelect = (state: string) => {
    setFormData({ ...formData, state, district: "" });
    dispatch(fetchDistrictsByStateName(state));
  };

  const handleDistrictSelect = (district: string) => {
    setFormData({ ...formData, district });
  };

  const handleAddToCart = async () => {
    if (!selectedSlot) {
      alert("Please select a time slot.");
      return;
    }

    const product = selectedSeller?.digitalProducts?.find(p => p._id === productId);
    const sellerIdStr = typeof selectedSeller?.sellerId === "object" ? (selectedSeller.sellerId as any)?.sellerId || (selectedSeller.sellerId as any)?._id : selectedSeller?.sellerId;
    const sellerName = typeof selectedSeller?.sellerId === "object" ? (selectedSeller.sellerId as any)?.name : (selectedSeller?.sellerId || "Consultation");

    const payload = {
      productId: productId!,
      productName: product?.name || "Consultation",
      productType: ProductType.DIGITAL,
      quantity: 1,
      unitPrice: product?.price || 500,
      sellerId: sellerIdStr || sellerId!,
      sellerName: sellerName,
      metadata: {
        patientName: `${formData.firstName} ${formData.lastName}`,
        patientGender: formData.gender,
        patientDob: formData.dob ? format(formData.dob, "yyyy-MM-dd") : "",
        appointmentDate: format(selectedDate, "yyyy-MM-dd"),
        appointmentTime: selectedSlot,
        consultationMode: formData.consultationMode,
        contactMobile: formData.mobile,
        contactEmail: formData.email,
        address: `${formData.address}, ${formData.city}, ${formData.state}, ${formData.country} - ${formData.pincode}`
      }
    };

    try {
      await addItemToCart(payload);
      alert("Appointment added to cart!");
      navigate("/cart");
    } catch (err) {
      alert("Failed to add to cart: " + err);
    }
  };

  const countryOptions = countries.map((c) => c.countryName).sort((a, b) => a.localeCompare(b));
  const stateOptions = states.map((s) => s.stateName).sort((a, b) => a.localeCompare(b));
  const districtOptions = districts.map((d) => d.districtName).sort((a, b) => a.localeCompare(b));

  const productPrice = selectedSeller?.digitalProducts?.find(p => p._id === productId)?.price || 500;

  return (
    <>
      {/* ── DESKTOP (display-max) ── */}
      <div className="display-max">
        <Navbar />
        <CategoriesBar />
        <div className="container w-50 mt-3 pb-5">
          <div className="card border-radius-lg shadow-dark p-5">
            <div>
              <img src="../../assets/img/file.png" className="opacity-6 mb-2" height={50} alt="" />
            </div>
            <b className="text-black fs-4 fw-normal letter-spacing-1">Add Patient Details</b>
            <div><b className="text-black fw-normal">Fill in the patient details below:</b></div>

            <div className="mt-4">
              <label className="form-label text-black fw-bold">Name as per Govt ID</label>
              <div className="d-flex gap-3 mb-3">
                <div className="w-100">
                  <div className="input-group input-group-outline mb-3 focused is-focused">
                    <input name="firstName" value={formData.firstName} onChange={handleInputChange} className="form-control-md fs-6 w-100" placeholder="First Name" />
                  </div>
                </div>
                <div className="w-100">
                  <div className="input-group input-group-outline mb-3 focused is-focused">
                    <input name="lastName" value={formData.lastName} onChange={handleInputChange} className="form-control-md fs-6 w-100" placeholder="Last Name" />
                  </div>
                </div>
              </div>

              <div className="d-flex gap-3 mb-3">
                <div className="w-100">
                  <label className="form-label text-black fw-bold">Gender</label>
                  <select 
                    name="gender" 
                    value={formData.gender} 
                    onChange={(e) => setFormData({...formData, gender: e.target.value})} 
                    className="form-select border p-2"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Others">Others</option>
                  </select>
                </div>
                <div className="w-100">
                  <label className="form-label text-black fw-bold">Date of Birth</label>
                  <DatePicker value={formData.dob} onChange={(d) => setFormData({...formData, dob: d})} placeholder="Select DOB" />
                </div>
              </div>

              <div className="d-flex gap-3 mb-3">
                <div className="w-100">
                  <label className="form-label text-black fw-bold">Mobile Number</label>
                  <div className="input-group input-group-outline focused is-focused">
                    <input name="mobile" value={formData.mobile} onChange={handleInputChange} className="form-control-md fw-normal w-100" placeholder="Mobile Number" />
                  </div>
                </div>
                <div className="w-100">
                  <label className="form-label text-black fw-bold">Email ID</label>
                  <div className="input-group input-group-outline focused is-focused">
                    <input name="email" value={formData.email} onChange={handleInputChange} className="form-control-md fw-normal w-100" placeholder="Email Address" />
                  </div>
                </div>
              </div>

              <div className="d-flex gap-3 mb-3">
                <div className="w-100">
                  <label className="form-label text-black fw-bold">Address</label>
                  <div className="input-group input-group-outline focused is-focused">
                    <input name="address" value={formData.address} onChange={handleInputChange} className="form-control-md fw-normal w-100" placeholder="Full Address" />
                  </div>
                </div>
                <div className="w-100">
                  <label className="form-label text-black fw-bold">Area / Locality</label>
                  <div className="input-group input-group-outline focused is-focused">
                    <input name="address" value={formData.address} onChange={handleInputChange} className="form-control-md fw-normal w-100" placeholder="Area / Locality" />
                  </div>
                </div>
              </div>

              <div className="d-flex gap-3 mb-3">
                <SearchDropdown label="Country" value={formData.country} options={countryOptions} onSelect={handleCountrySelect} loading={locLoading && countries.length === 0} />
                <SearchDropdown label="State" value={formData.state} options={stateOptions} onSelect={handleStateSelect} disabled={!formData.country} loading={locLoading && !!formData.country && states.length === 0} />
              </div>

              <div className="d-flex gap-3 mb-3">
                <SearchDropdown label="District" value={formData.district} options={districtOptions} onSelect={handleDistrictSelect} disabled={!formData.state} loading={locLoading && !!formData.state && districts.length === 0} />
                <div className="w-100">
                  <div className={`input-group input-group-outline mb-3 ${formData.city ? "focused is-focused" : ""}`}>
                    <label className="form-label fw-bold text-black mt-0">City</label>
                    <input name="city" value={formData.city} onChange={handleInputChange} className="form-control" placeholder="City" />
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <div className={`input-group input-group-outline mb-3 ${formData.pincode ? "focused is-focused" : ""}`}>
                    <label className="form-label fw-bold text-black mt-0">Pincode</label>
                    <input name="pincode" value={formData.pincode} onChange={handleInputChange} className="form-control" placeholder="Pincode" />
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label text-black fw-bold">Consultation Mode</label>
                  <div className="d-flex gap-3 mt-1">
                    <label className="cursor-pointer text-sm">
                      <input type="radio" name="consultationMode" value="Physical" checked={formData.consultationMode === "Physical"} onChange={handleInputChange} className="me-2" />
                      Physical Visit
                    </label>
                    <label className="cursor-pointer text-sm">
                      <input type="radio" name="consultationMode" value="Online" checked={formData.consultationMode === "Online"} onChange={handleInputChange} className="me-2" />
                      Online Consultation
                    </label>
                  </div>
                </div>
              </div>

              <div className="text-center mt-5">
                <button className="btn btn-dark btn-lg border-radius-section px-5" data-bs-toggle="offcanvas" data-bs-target="#offcanvasRight">
                  Select Slot & Book
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── MOBILE (display-mini/mob) ── */}
      <div className="display-mini display-mob pb-7">
        <Navbar />
        <div className="pt-8 container-fluid">
          <div className="card w-100 border-radius-lg shadow-dark p-3">
            <div><img src="../../assets/img/file.png" className="opacity-6 mb-2" height={50} alt="" /></div>
            <b className="text-black fs-4 fw-normal letter-spacing-1">Add Patient Details</b>
            <div><b className="text-black fw-normal">Fill in the patient details below:</b></div>

            <div className="mt-4">
              <label className="form-label text-black fw-bold">Name as per Govt ID</label>
              <div className="input-group input-group-outline mb-3 focused is-focused">
                <input name="firstName" value={formData.firstName} onChange={handleInputChange} className="form-control-md fs-6 w-100" placeholder="Enter First Name" />
              </div>
              <div className="input-group input-group-outline mb-3 focused is-focused">
                <input name="lastName" value={formData.lastName} onChange={handleInputChange} className="form-control-md fs-6 w-100" placeholder="Enter Last Name" />
              </div>

              <div className="mb-3">
                <label className="form-label text-black fw-bold">Gender</label>
                <select name="gender" value={formData.gender} onChange={(e) => setFormData({...formData, gender: e.target.value})} className="form-select border p-2">
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label text-black fw-bold">Date of Birth</label>
                <DatePicker value={formData.dob} onChange={(d) => setFormData({...formData, dob: d})} placeholder="Select DOB" />
              </div>

              <div className="mb-3">
                <label className="form-label text-black fw-bold">Mobile Number</label>
                <div className="input-group input-group-outline focused is-focused">
                  <input name="mobile" value={formData.mobile} onChange={handleInputChange} className="form-control-md fw-normal w-100" placeholder="Enter Mobile Number" />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label text-black fw-bold">Email ID</label>
                <div className="input-group input-group-outline focused is-focused">
                  <input name="email" value={formData.email} onChange={handleInputChange} className="form-control-md fw-normal w-100" placeholder="Enter Email ID" />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label text-black fw-bold">Address</label>
                <div className="input-group input-group-outline focused is-focused">
                  <input name="address" value={formData.address} onChange={handleInputChange} className="form-control-md fw-normal w-100" placeholder="Enter Address" />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label text-black fw-bold">Area / Locality</label>
                <div className="input-group input-group-outline focused is-focused">
                  <input name="address" value={formData.address} onChange={handleInputChange} className="form-control-md fw-normal w-100" placeholder="Enter Area / Locality" />
                </div>
              </div>

              <SearchDropdown label="Country" value={formData.country} options={countryOptions} onSelect={handleCountrySelect} loading={locLoading && countries.length === 0} />
              <SearchDropdown label="State" value={formData.state} options={stateOptions} onSelect={handleStateSelect} disabled={!formData.country} loading={locLoading && !!formData.country && states.length === 0} />
              <SearchDropdown label="District" value={formData.district} options={districtOptions} onSelect={handleDistrictSelect} disabled={!formData.state} loading={locLoading && !!formData.state && districts.length === 0} />

              <div className="mb-3">
                <div className={`input-group input-group-outline ${formData.city ? "focused is-focused" : ""}`}>
                  <label className="form-label fw-bold text-black mt-0">City</label>
                  <input name="city" value={formData.city} onChange={handleInputChange} className="form-control" placeholder="Select City" />
                </div>
              </div>

              <div className="mb-3">
                <div className={`input-group input-group-outline ${formData.pincode ? "focused is-focused" : ""}`}>
                  <label className="form-label fw-bold text-black mt-0">Pincode</label>
                  <input name="pincode" value={formData.pincode} onChange={handleInputChange} className="form-control" placeholder="Enter Pincode" />
                </div>
              </div>

              <div className="mb-4">
                <label className="form-label text-black fw-bold">Consultation Mode</label>
                <div className="d-flex flex-column gap-2 mt-1">
                  <label className="cursor-pointer">
                    <input type="radio" name="consultationMode" value="Physical" checked={formData.consultationMode === "Physical"} onChange={handleInputChange} className="me-2" />
                    Physical Visit
                  </label>
                  <label className="cursor-pointer">
                    <input type="radio" name="consultationMode" value="Online" checked={formData.consultationMode === "Online"} onChange={handleInputChange} className="me-2" />
                    Online Consultation
                  </label>
                </div>
              </div>

              <div className="text-center">
                <button className="btn btn-dark w-100 btn-lg rounded-pill" data-bs-toggle="offcanvas" data-bs-target="#offcanvasBottom">
                  Select Slot & Book
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Desktop Selection Offcanvas ── */}
      <div className="offcanvas offcanvas-end w-25" tabIndex={-1} id="offcanvasRight" aria-labelledby="offcanvasRightLabel">
        <div className="d-flex justify-content-between align-items-start p-3 border-bottom">
          <h5 id="offcanvasRightLabel">Slot Booking</h5>
          <button type="button" className="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        <div className="offcanvas-body p-4">
          <div className="card border-radius-lg mt-2 mb-2 shadow-dark p-2">
            <div className="d-flex justify-content-between align-items-center">
              <div className="w-100">
                <div className="d-flex justify-content-start align-items-center gap-3">
                  <div>
                    <img src={selectedSeller?.banners?.[0]?.url || "../../assets/img/team-2.jpg"} alt="Consultation" className="img-fluid border-radius-section" style={{ width: "70px", height: "70px", objectFit: "cover" }} />
                  </div>
                  <div className="lh-sm">
                    <div>
                      <b className="text-black fw-bold fs-6">
                        {typeof selectedSeller?.sellerId === "object" ? (selectedSeller.sellerId as any)?.name : (selectedSeller?.sellerId || "Consultation")}
                      </b>
                      <div>
                        <b className="text-black fw-normal text-xs letter-spacing-1">
                          {selectedSeller?.businessInfo?.businessInfo.department || "Consultant"}
                        </b>
                      </div>
                      
                      <div>
                        <b className="text-black fw-normal text-xs letter-spacing-1">
                          <i className="fi fi-sr-marker text-xs"></i>{" "}
                          {selectedSeller?.businessInfo?.businessInfo.city || "Unknown Location"}
                        </b>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ul className="nav nav-tabs w-100 mt-4 border-radius-section" role="tablist">
            <div className="d-flex w-100 justify-content-between align-items-center gap-4 overflow-sides no-scrollbar" style={{ overflowX: "auto" }}>
              {dates.map((date, i) => (
                <div className="w-100" key={i}>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link w-100 text-nowrap ${activeDateIndex === i ? "active" : ""}`}
                      onClick={() => {
                        setActiveDateIndex(i);
                        setSelectedDate(date);
                      }}
                      type="button"
                    >
                      <div className="lh-sm">
                        <b className="text-black fw-bolder text-sm">{format(date, "dd MMM")}</b>
                        <div>
                          <b className="text-black fw-normal text-xs letter-spacing-1 text-uppercase">{format(date, "EEEE")}</b>
                        </div>
                      </div>
                    </button>
                  </li>
                </div>
              ))}
            </div>
          </ul>

          <div className="tab-content mt-3">
            <TimeSlots 
              selectedSlot={selectedSlot} 
              onSlotSelect={setSelectedSlot} 
              config={currentProduct?.appointmentConfiguration} 
              selectedDate={selectedDate}
            />
          </div>
        </div>

        <div className="offcanvas-footer border-top">
          <div className="p-2 px-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="w-100">
                <div>
                  <b className="text-black fw-normal fs-5">Price</b>
                  <div>
                    <b className="text-black fw-bolder fs-6">
                      ₹{productPrice.toFixed(2)}{" "}
                      <span className="text-black-50 text-xxs">(inclusive of all taxes)</span>
                    </b>
                  </div>
                </div>
              </div>
              <div className="w-100 text-end">
                <button className="btn btn-lg btn-dark w-90" onClick={handleAddToCart}>
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile Selection Offcanvas ── */}
      <div className="offcanvas offcanvas-bottom h-75" tabIndex={-1} id="offcanvasBottom" aria-labelledby="offcanvasBottomLabel">
        <div className="d-flex justify-content-between align-items-start p-3 border-bottom">
          <h5 id="offcanvasBottomLabel">Slot Booking</h5>
          <button type="button" className="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        <div className="offcanvas-body">
          <div className="card border-radius-lg mt-2 w-95 mx-auto mb-2 shadow-dark p-2">
            <div className="d-flex justify-content-between align-items-center">
              <div className="w-100">
                <div className="d-flex justify-content-start align-items-center gap-3">
                  <div>
                    <img src={selectedSeller?.banners?.[0]?.url || "../../assets/img/team-2.jpg"} alt="Consultation" className="img-fluid border-radius-section" style={{ width: "70px", height: "70px", objectFit: "cover" }} />
                  </div>
                  <div className="lh-sm">
                    <div>
                      <b className="text-black fw-bold fs-6">
                        {typeof selectedSeller?.sellerId === "object" ? (selectedSeller.sellerId as any)?.name : (selectedSeller?.sellerId || "Consultation")}
                      </b>
                      <div>
                        <b className="text-black fw-normal text-xs letter-spacing-1">
                          {selectedSeller?.businessInfo?.businessInfo.department || "Consultant"}
                        </b>
                      </div>
                      <div>
                        <b className="text-black fw-normal text-xs letter-spacing-1">
                          <i className="fi fi-sr-marker text-xs"></i>{" "}
                          {selectedSeller?.businessInfo?.businessInfo.city || "Unknown Location"}
                        </b>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ul className="nav nav-tabs w-100 mt-4 px-2" role="tablist">
            <div className="d-flex w-100 justify-content-between align-items-center gap-4 overflow-sides no-scrollbar" style={{ overflowX: "auto" }}>
              {dates.map((date, i) => (
                <div className="w-100" key={i}>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link w-100 text-nowrap ${activeDateIndex === i ? "active" : ""}`}
                      onClick={() => {
                        setActiveDateIndex(i);
                        setSelectedDate(date);
                      }}
                      type="button"
                    >
                      <div className="lh-sm">
                        <b className="text-black fw-bolder text-sm">{format(date, "dd MMM")}</b>
                        <div>
                          <b className="text-black fw-normal text-xs letter-spacing-1 text-uppercase">{format(date, "EEEE")}</b>
                        </div>
                      </div>
                    </button>
                  </li>
                </div>
              ))}
            </div>
          </ul>

          <div className="tab-content mt-3 px-2">
            <TimeSlots 
              selectedSlot={selectedSlot} 
              onSlotSelect={setSelectedSlot} 
              config={currentProduct?.appointmentConfiguration} 
              selectedDate={selectedDate}
            />
          </div>
        </div>

        <div className="offcanvas-footer border-top">
          <div className="p-2 px-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="w-100">
                <div>
                  <b className="text-black fw-normal fs-5">Price</b>
                  <div>
                    <b className="text-black fw-bolder fs-6">
                      ₹{productPrice.toFixed(2)}{" "}
                      <span className="text-black-50 text-xxs">(inclusive of all taxes)</span>
                    </b>
                  </div>
                </div>
              </div>
              <div className="w-50 text-end">
                <button className="btn btn-lg btn-dark w-100" onClick={handleAddToCart}>
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerDetails;
